import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { PackagingProductProps, PriceProductProps, ProductProps } from '@/types';
import { useCalcPrice, useDbFunctions, useComboCartItems } from '@/talons';
import { OrderPayloadProps } from './type';
import { useNavigate } from 'react-router-dom';
import { CartItems } from '@/hooks/CartContext';
import { StockProps } from '@/hooks/StockContext';
import { useLoader } from '@/hooks/LoaderContext';
import cartItemsMock from '@/mocks/cartItemsMocks';
import {
  buildCartDetailsKey,
  getCartBrandKeyFromStorage,
  getOnStorage,
  getOrderNumberFromStorage,
  normalizeParametroPrecoBase,
  findDirectPriceRecord,
  isNroSegmentoPrecoCuringa,
  setOnStorage,
} from '@/shared';
import * as ordersStore from '@/shared/ordersStore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HandlePrices } from '@/shared/calculatePrices';
import shippingMethod from '@/mocks/shippingMethodMocks';
import { useEvents } from '../use-events/useEvents';
import {
  buildComboDisplaySplitsForCartLine,
  getActiveComboPromotionTypeForCartItem,
  getSliceAllocationsForCartLine,
  type ComboCartItemAllocation,
} from '../combos/comboCartUtils';
import { isActiveComboPortionFromAllocations } from '../combos/comboFlexEligibility';
import {
  buildCheckoutLinePriceWithCombo,
  buildCheckoutIntegrationPriceWithCombo,
  buildCheckoutDisplayPricingForEntry,
  buildComboReasonForCartItem,
  resolveCartLineUnitPrice,
} from '../combos/comboCheckoutUtils';
import { useComboCheckoutPriceDeps, getCheckoutPaymentConditions } from '../combos/useComboCheckoutPriceDeps';
import {
  computeCheckoutLineStPricing,
  computeStLineTotalLikeStPrice,
  getComboPriceRecordForCartLine,
  getNormalPriceRecordForCartLine,
  resolveStFromPriceRecord,
} from '../combos/comboCartTotalsUtils';

import { useProductDictionary } from '@/talons/productCatalog';
import {
  useCart,
  useStock,
  useClient,
  useSelectedClientScope,
  useGlobals,
  useFetchAxios,
  useCartTotals,
  useUserContext,
  useDiscountFlex,
  useDiscountAmountFlex,
  useNetworkStatusContext,
  useTables,
} from '@/hooks';

interface CartProductItem {
  item: CartItems;
  product: ProductProps | undefined;
}

type QaConfigCheckout = {
  enabled: boolean; // ativa/desativa modo QA
  skus: string[]; // lista de SKUs alvo; vazio = aplica em todos os itens elegíveis
  forcePercentual?: number; // força percentual_flex (ex: 5 -> "5.00")
  missingIntegration?: boolean; // remove o campo integration_price_flex (simula “sem integral price”)
};

declare global {
  interface Window {
    qaConfigCheckout?: QaConfigCheckout;
  }
}

export const useCheckoutPage = () => {
  const { tables } = useTables();
  // Produtos por código, abertos sob demanda — o dicionário inteiro não fica mais no contexto.
  const { products: productsDict } = useProductDictionary();
  const navigate = useNavigate();
  const db = useDbFunctions();
  const { registerEvent } = useEvents(db);
  const { updateTable } = useLoader();
  const { axiosCtx } = useFetchAxios();
  const { handlePrices, findPrice, getPrices } = useCalcPrice();
  const { handleUpdateStock, stockLeft } = useStock();
  const { cartTotals, listaTributacao } = useCartTotals();
  const { comboCartItemsByUid, isComboCompleteById, combos } = useComboCartItems();
  const { discountAmountClient, estimatedSaldoFlex } = useDiscountAmountFlex();
  const { connectivityStatus, checkConnectivity } = useNetworkStatusContext();
  const { cartDetails, handleRemoveItems, getLimit, getOrGenerateOrderNumber } = useCart();
  const { setRcaSaldoFlex: setFlex, rcaSaldoFlex } = useUserContext();
  const { clearClientDiscount, getDiscountProduct } = useDiscountFlex();
  const { version, paymentCode, setPaymentCode, setCanAccessSuccess, setOrderData, setCartTotals, setOrderItems } = useGlobals();
  const { selectedClientAddress, selectedClient, setSelectedClient, calculateDeliveryDate, setJanelaEntregaData, janelaEntregaData } = useClient();

  // 🔧 Configuração de QA simples e global (o QA pode alterar via console)
  window.qaConfigCheckout = window.qaConfigCheckout || {
    enabled: false,
    skus: [],
    forcePercentual: 5,
    missingIntegration: false,
  };
  const qaConfig = window.qaConfigCheckout;

  const getStoredOrderNumber = useCallback((client: any) => {
    if (!client?.COD_CLIENTE) return null;
    const storedOrders = getOnStorage('order_numbers') || {};
    const brandKey = getCartBrandKeyFromStorage();
    return getOrderNumberFromStorage(client.COD_CLIENTE, brandKey, storedOrders) ?? null;
  }, []);

  const [, setSucess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erpNumber, setErpNumber] = useState('');

  const creditLimit = selectedClient?.SALDO_DISPONIVEL;

  const selectedClientScope = useSelectedClientScope();

  // Normaliza qualquer valor numérico vindo como string (ex.: "1.234,56", "R$ 123,45") ou number
  const toNumber = useCallback((v: unknown): number => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    if (typeof v !== 'string') return 0;
    const s = v.trim();
    if (!s) return 0;
    // Remove símbolo de moeda e espaços
    let cleaned = s.replace(/\s|R\$|\u00A0/g, '');
    // Se contiver vírgula e não contiver ponto, assuma vírgula como separador decimal
    if (/,/.test(cleaned) && !/\./.test(cleaned)) {
      cleaned = cleaned.replace(/\./g, ''); // remove milhares por segurança
      cleaned = cleaned.replace(/,/g, '.');
    } else {
      // Caso tenha ambos, assume o último como separador decimal
      const lastComma = cleaned.lastIndexOf(',');
      const lastDot = cleaned.lastIndexOf('.');
      if (lastComma > lastDot) {
        // vírgula como decimal: remove pontos de milhar e troca vírgula por ponto
        cleaned = cleaned.replace(/\./g, '').replace(/,/g, '.');
      }
    }
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  }, []);

  const listPaymentCode = useMemo(() => {
    return selectedClientScope?.COD_PAGAMENTO || [];
  }, [selectedClientScope?.COD_PAGAMENTO]);

  const productList = useMemo(() => {
    if (!selectedClientScope) return [];

    const codLoja = selectedClientScope?.COD_LOJA;
    const tabVenda = selectedClientScope?.COD_TABELA_VENDA;
    const condicoes = getCheckoutPaymentConditions(
      selectedClientScope?.COD_PAGAMENTO,
      selectedClient?.COD_PAGAMENTO
    );

    const catalog = productsDict;
    const listSubstTrib = tables.current.cod_regime_tributario;

    const parametroPrecoBase = normalizeParametroPrecoBase(
      tables.current.parametro_preco_base,
      codLoja,
      tabVenda,
      condicoes
    );

    const percentualfornecedor = tables.current.percentual_fornecedor ?? [];
    const percentualFornecedor = percentualfornecedor.filter((p) => `${p.NROTABVENDA}` === `${tabVenda}`);

    const parametroPrecoPromocao = tables.current.precoPromocaoCliente;

    // Linha sem quantidade não é item do pedido — carrinhos gravados antes da CLI-5824 podem trazê-la.
    const sellableItems = cartDetails?.[selectedClientScope?.COD_CLIENTE]?.items?.filter(
      (item) => Number(item?.quantity) > 0
    );
    const items = sellableItems?.map((item) => {
      const product = catalog?.[item?.sku];
      if (!product) return null;
      const calculatedPrices = getPrices(product);
      const productWithPrice = {
        ...product,
        PRECO:
          calculatedPrices.length > 0
            ? calculatedPrices
            : handlePrices(
                condicoes,
                product,
                parametroPrecoBase,
                percentualFornecedor,
                listSubstTrib,
                parametroPrecoPromocao,
                'checkout'
              ),
      };
      return { item, product: productWithPrice };
    });

    type ItemEntry = { item: CartItems; product: ProductProps };
    return (items?.filter((entry): entry is ItemEntry => entry != null) ?? []) as CartProductItem[];
  }, [cartDetails, productsDict, getPrices, handlePrices, selectedClient, selectedClientScope, tables]);

  const checkoutComboPriceDeps = useComboCheckoutPriceDeps();

  const conditions = useMemo(() => listPaymentCode || null, [listPaymentCode]);

  const shippingData = useMemo(() => {
    if (selectedClientAddress && shippingMethod) {
      const deliveryDate = calculateDeliveryDate();
      return [
        { dataType: 'address', ...selectedClientAddress },
        { dataType: 'shipping', deliveryDate, ...shippingMethod },
      ];
    }
    return [];
  }, [selectedClientAddress, calculateDeliveryDate]);

  useEffect(() => {
    if (!selectedClient?.COD_CLIENTE || !shippingMethod) return;

    const deliveryDate = calculateDeliveryDate();
    if (!deliveryDate || deliveryDate === janelaEntregaData) return;

    setJanelaEntregaData(deliveryDate);
  }, [selectedClient?.COD_CLIENTE, calculateDeliveryDate, setJanelaEntregaData, janelaEntregaData]);

  const cartItems = useMemo(() => cartItemsMock || null, []);

  const generateOrderPayload = useCallback(() => {
    if (!productList || !selectedClientScope || !cartDetails || !paymentCode) {
      return null;
    }

    const buildPayloadItemForEntry = (
      entry: CartProductItem,
      allocations: ComboCartItemAllocation[] | undefined
    ) => {
      const [pack, qtd] = entry?.item?.option_uid?.split('_');
      const qty = Number(qtd);

      const clientCodSegmentos = selectedClientScope?.COD_SEGMENTO?.map((s) => s.cod_segmento) ?? [];

      const rawPrecos: any = entry?.product?.PRECO || {};
      const prices: PriceProductProps[] = Array.isArray(rawPrecos)
        ? rawPrecos
        : Object.values(rawPrecos || {});
      const list: any[] = prices;
      const listByLoja = list.filter((i) => i.COD_LOJA === selectedClientScope?.COD_LOJA);

      const preco =
        findDirectPriceRecord({
          prices: listByLoja,
          pack: pack as any,
          packQty: qty,
          paymentCode,
          selectedClient: selectedClientScope,
        }) ?? ({} as any);

      const matchesOptionUid = (e: PackagingProductProps) =>
        pack === 'PK'
          ? e.SG_EMBALAGEM === pack && e.QTD_MULTIPLO_VENDA === qty
          : e.SG_EMBALAGEM === pack && e.QTD_EMBALAGEM === qty;

      const candidatosEmbalagem = (entry?.product?.EMBALAGEM ?? []).filter(
        (e) => clientCodSegmentos.includes(e.COD_SEGMENTO) && matchesOptionUid(e)
      );

      let embalagemDaLinha: PackagingProductProps | undefined;
      if (candidatosEmbalagem.length === 1) {
        embalagemDaLinha = candidatosEmbalagem[0];
      } else if (candidatosEmbalagem.length > 1) {
        const peloPreco = candidatosEmbalagem.find(
          (e) => preco?.NRO_SEGMENTO != null && Number(e.COD_SEGMENTO) === Number(preco.NRO_SEGMENTO)
        );
        embalagemDaLinha =
          peloPreco ??
          [...candidatosEmbalagem].sort(
            (a, b) =>
              clientCodSegmentos.indexOf(a.COD_SEGMENTO) - clientCodSegmentos.indexOf(b.COD_SEGMENTO)
          )[0];
      }

      let cod_segmento: number | undefined = embalagemDaLinha?.COD_SEGMENTO;
      if (cod_segmento == null && preco?.NRO_SEGMENTO != null) {
        const n = Number(preco.NRO_SEGMENTO);
        if (isNroSegmentoPrecoCuringa(n) || clientCodSegmentos.includes(n)) {
          cod_segmento = n;
        }
      }
      if (cod_segmento == null) {
        cod_segmento = selectedClientScope?.COD_SEGMENTO?.[0]?.cod_segmento;
      }

      const baseIntegrationPrice =
        Number(preco?.VLR_PRECO_CALCULADO) / (Number(preco?.QTD_MULTIPLO_VENDA) || 1);

      const promotionType = getActiveComboPromotionTypeForCartItem(
        entry.item,
        allocations,
        isComboCompleteById
      );

      // Porção com combo ATIVO ignora o flex (ver ./comboFlexEligibility) — o desconto de combo vale
      // sozinho. O flex persistido continua salvo, só deixa de ser lido aqui enquanto o combo for ativo.
      const isComboActivePortion = isActiveComboPortionFromAllocations(entry.item, allocations);
      const flexLookup = isComboActivePortion ? () => undefined : getDiscountProduct;

      const flexIntegrationPrice = flexLookup({
        productId: entry?.product?.COD_PRODUTO || 0,
        package: pack,
        quantity: qty,
        uid: entry.item.uid,
      });
      const percentualFlexNum = Number(flexIntegrationPrice?.percentual) || 0;
      const percentualFlexStr = flexIntegrationPrice
        ? (percentualFlexNum.toFixed(2) || '0').toString()
        : '0';

      const priceRecord = findPrice(prices, entry.item.option_uid, paymentCode);
      const baseUnitPrice = Number(priceRecord?.VLR_FINAL || preco?.VLR_FINAL || 0);
      const normalUnitPrice =
        resolveCartLineUnitPrice(
          entry.item,
          prices,
          paymentCode,
          findPrice,
          flexLookup,
          selectedClientScope,
          listaTributacao
        ) ?? baseUnitPrice;

      // Sem combo, usa o valor persistido pelo modal; com combo, recalcula sobre o preço desta porção.
      const rawFlexDiscount = flexIntegrationPrice?.discountPrice?.[`${paymentCode}`];
      const hasPersistedFlexDiscount = rawFlexDiscount !== undefined && rawFlexDiscount !== null;

      let unitPriceFlexResolved = 0;
      if (!allocations?.length && hasPersistedFlexDiscount) {
        unitPriceFlexResolved = Number(rawFlexDiscount);
      } else {
        const flexBaseRecord =
          (allocations?.length
            ? getComboPriceRecordForCartLine(
                entry.item,
                allocations,
                entry.product as ProductProps,
                paymentCode,
                checkoutComboPriceDeps
              )
            : getNormalPriceRecordForCartLine(
                entry.item,
                entry.product as ProductProps,
                paymentCode,
                checkoutComboPriceDeps
              )) ?? preco;

        const precoCalculadoFlexUnit =
          Number(flexBaseRecord?.VLR_PRECO_CALCULADO) * (1 + percentualFlexNum / 100);
        const stLineTotalFlex = computeStLineTotalLikeStPrice(
          flexBaseRecord,
          entry.item.quantity || 0,
          selectedClient,
          listaTributacao,
          percentualFlexNum
        );
        unitPriceFlexResolved =
          entry.item.quantity > 0 && Number.isFinite(precoCalculadoFlexUnit)
            ? precoCalculadoFlexUnit + stLineTotalFlex / entry.item.quantity
            : 0;
      }

      const priceFlex =
        percentualFlexNum !== 0 && unitPriceFlexResolved > 0
          ? Number(unitPriceFlexResolved.toFixed(2))
          : 0;

      const comboLinePricing = buildCheckoutLinePriceWithCombo(
        entry.item,
        normalUnitPrice,
        allocations,
        entry.product as ProductProps,
        paymentCode,
        qty,
        checkoutComboPriceDeps
      );

      const integrationPrice = buildCheckoutIntegrationPriceWithCombo(
        entry.item,
        allocations,
        entry.product as ProductProps,
        paymentCode,
        checkoutComboPriceDeps,
        baseIntegrationPrice
      );

      const valorAjuste = (percentualFlexNum / 100) * integrationPrice;
      let integrationPriceFlexNum = Number(((integrationPrice + valorAjuste) * 100) / 100);
      const totalPriceFlex = priceFlex * entry.item.quantity;
      const unitPriceFlexPerItem = qty > 0 ? (priceFlex / qty).toFixed(2) : '0';
      const precoPauta = preco?.VLR_PAUTA || 0;
      const tributacao = preco
        ? HandlePrices.getTributacao(selectedClientScope, preco.COD_TRIBUTACAO_PRODUTO, listaTributacao)
        : undefined;
      const precoFlexST = HandlePrices.CalculaST(
        tributacao,
        integrationPriceFlexNum * entry.item.quantity,
        precoPauta
      );
      let percentualFlexOut = percentualFlexStr;
      const hasFlex = Number(percentualFlexOut) !== 0 || !!flexIntegrationPrice?.percentual;
      const isTargetSku =
        qaConfig.enabled && (!qaConfig.skus?.length || qaConfig.skus.includes(String(entry?.item?.sku)));

      if (isTargetSku && hasFlex) {
        integrationPriceFlexNum = 0;

        if (typeof qaConfig.forcePercentual === 'number' && !Number.isNaN(qaConfig.forcePercentual)) {
          percentualFlexOut = qaConfig.forcePercentual.toFixed(2);
        } else if (percentualFlexOut === '0') {
          percentualFlexOut = '5.00';
        }
      }

      const comboReason = buildComboReasonForCartItem(allocations, combos);

      let stPricing = computeCheckoutLineStPricing(
        entry.item,
        allocations,
        entry.product as ProductProps,
        paymentCode,
        checkoutComboPriceDeps,
        selectedClient,
        listaTributacao,
        Number(percentualFlexStr)
      );

      if (
        stPricing.stLineTotal <= 0 &&
        preco &&
        (preco.ST_VLR_FINAL || preco.VLR_PRECO_CALCULADO)
      ) {
        stPricing = resolveStFromPriceRecord(
          preco,
          entry.item.quantity || 0,
          selectedClient,
          listaTributacao,
          Number(percentualFlexStr)
        );
      }

      const baseItem = {
        cod_segmento,
        cod_produto: entry?.product?.COD_PRODUTO,
        unilever: entry?.product?.UNILEVER,
        sku: entry?.item?.sku.toString(),
        product: entry?.product?.DESC_PRODUTO || '',
        price: comboLinePricing.lineTotal > 0 ? comboLinePricing.lineTotal.toFixed(2) : '',
        unit_price: comboLinePricing.unitPrice > 0 ? comboLinePricing.unitPrice.toFixed(2) : '',
        unit_price_per_item:
          comboLinePricing.unitPricePerItem > 0 ? comboLinePricing.unitPricePerItem.toFixed(2) : '',
        st_price: stPricing.stLineTotal > 0 ? stPricing.stLineTotal.toFixed(2) : '',
        integration_price: integrationPrice,
        quantity: entry?.item?.quantity || 0,
        packaging: entry?.item?.option_uid || '',
        st_price_packaging: stPricing.stPerPackaging || 0,
        ...(promotionType ? { promotion_type: promotionType } : {}),

        // FLEX
        unit_price_flex: hasFlex ? priceFlex.toFixed(2) : '0',
        // integration_price_flex será ajustado abaixo para suportar remoção do campo no caso QA
        integration_price_flex: hasFlex ? integrationPriceFlexNum.toFixed(2) : '0',
        unit_price_per_item_flex: hasFlex ? unitPriceFlexPerItem : '0',
        st_price_flex: hasFlex ? precoFlexST : 0,
        price_flex: hasFlex ? totalPriceFlex.toFixed(2) : '0',
        percentual_flex: Number(percentualFlexOut) !== 0 ? percentualFlexOut : '0',

        quantity_multiplo: preco?.QTD_MULTIPLO_VENDA || 1,
        status: '',
        reason: comboReason,
      } as any;

      // Se QA pediu para "remover" o campo integration_price_flex, deletamos do objeto final:
      if (isTargetSku && hasFlex && qaConfig.enabled && qaConfig.missingIntegration) {
        delete baseItem.integration_price_flex; // @ts-ignore
      }

      return baseItem;
    };

    const items = productList.flatMap((entry: CartProductItem) => {
      const allocations = comboCartItemsByUid[entry.item.uid];
      // Mesma divisão por instância de combo (kit) que o carrinho exibe — cada porção usa seu próprio displayKey como chave do flex.
      const slices = buildComboDisplaySplitsForCartLine(entry.item, allocations);

      return slices.flatMap((slice) => {
        const instanceAllocations = getSliceAllocationsForCartLine(allocations, slice);

        const sliceQty = slice.displayQuantity || 0;
        const discountQty = slice.comboDiscountQuantity || 0;
        const regularQty = Math.max(sliceQty - discountQty, 0);

        if (discountQty > 0 && regularQty > 0) {
          const comboSubEntry: CartProductItem = {
            ...entry,
            item: { ...entry.item, quantity: discountQty, uid: slice.displayKey },
          };
          const regularSubEntry: CartProductItem = {
            ...entry,
            item: { ...entry.item, quantity: regularQty, uid: slice.displayKey },
          };

          return [
            buildPayloadItemForEntry(comboSubEntry, instanceAllocations),
            buildPayloadItemForEntry(regularSubEntry, undefined),
          ];
        }

        const subEntry: CartProductItem = {
          ...entry,
          item: { ...entry.item, quantity: sliceQty, uid: slice.displayKey },
        };

        return [buildPayloadItemForEntry(subEntry, discountQty > 0 ? instanceAllocations : undefined)];
      });
    });

    const storedOrderNumber = getStoredOrderNumber(selectedClientScope) || getOrGenerateOrderNumber();
    const cartOpenedAt = getOnStorage('cart_opened_at');

    if (selectedClientScope && items && cartDetails && storedOrderNumber) {
      return {
        version,
        origin_function: 'process',
        current_time: format(new Date(), 'yyyy-MM-dd HH:mm:ss', { locale: ptBR }),
        cart_opened_at: cartOpenedAt || '',
        company: {
          unilever: selectedClientScope.UNILEVER,
          name: selectedClientScope.NM_CLIENTE.toString(),
          id: selectedClientScope.COD_CLIENTE.toString(),
          cnpj: selectedClientScope.NR_DOCUMENTO.toString(),
          Cod_rca: selectedClientScope.COD_RCA_CLIENTE?.toString() || selectedClientScope.COD_RCA.toString(),
        },
        orders: {
          order_number: storedOrderNumber,
          erp_order_number: erpNumber ?? '',
          order_number_magento: '',
          origin: 'BateForte_web_mobile_rca',
          cod_loja: Number(selectedClientScope.COD_LOJA || 0),
          cod_segmento_loja: Number(selectedClientScope.COD_SEGMENTO?.[0]?.cod_segmento || 0),
          items: productList.length,
          status: '',
          reason: '',
          order_date: format(new Date(), 'EEEE, dd/MM/yyyy', { locale: ptBR }),
          total: Number(cartTotals?.total?.toFixed(2) ?? 0),
          payment_terms: paymentCode.toString(),
          actions_pending: items,
        },
      } as OrderPayloadProps;
    }
    return null;
  }, [
    productList,
    selectedClientScope,
    selectedClient,
    cartDetails,
    paymentCode,
    listaTributacao,
    getDiscountProduct,
    cartTotals?.total,
    erpNumber,
    version,
    getStoredOrderNumber,
    qaConfig,
    comboCartItemsByUid,
    isComboCompleteById,
    combos,
    checkoutComboPriceDeps,
    findPrice,
    handlePrices,
  ]);

  const total = useMemo(() => cartTotals?.total, [cartTotals?.total]);

  // Garante números válidos para o cálculo de crédito disponível
  const normalized = useMemo(() => {
    return {
      creditLimit: toNumber(creditLimit as any),
      total: toNumber(cartTotals?.total as any),
    };
  }, [creditLimit, cartTotals?.total, toNumber]);

  // Usamos o valor "bruto" para comparação (evita o caso de -0.00 após arredondar)
  const rawAvailableCredit = useMemo(() => normalized.creditLimit - normalized.total, [normalized]);

  const availableCredit = useMemo(() => Number(rawAvailableCredit.toFixed(2)), [rawAvailableCredit]);

  // Excedeu o limite se for realmente negativo OU se arredondou para -0 (Object.is detecta -0)
  const creditLimitExceeded = useMemo(
    () => rawAvailableCredit < 0 || Object.is(availableCredit, -0),
    [rawAvailableCredit, availableCredit]
  );

  const projectedSaldoFlex = estimatedSaldoFlex;
  const checkoutSaldoNegativo = projectedSaldoFlex < 0;

  const hasDiscountApplied = useMemo(() => Number(discountAmountClient) < 0, [discountAmountClient]);
  const blockByCredit = useMemo(
    // creditLimitExceeded já cobre o caso; mantemos estrutura para fácil extensão futura
    () => creditLimitExceeded,
    [creditLimitExceeded]
  );

  const disabled = useMemo(() => {
    return (
      cartDetails?.[selectedClient?.COD_CLIENTE]?.items?.length === 0 ||
      productList.length === 0 ||
      loading ||
      !selectedClient ||
      blockByCredit ||
      (checkoutSaldoNegativo && hasDiscountApplied)
    );
  }, [cartDetails, productList, selectedClient, blockByCredit, loading, checkoutSaldoNegativo]);

  const clearOrderNumber = useCallback(() => {
    if (!selectedClient?.COD_CLIENTE) return;
    const storedOrders = { ...(getOnStorage('order_numbers') || {}) };
    const scoped = buildCartDetailsKey(selectedClient.COD_CLIENTE, getCartBrandKeyFromStorage());
    if (!storedOrders[scoped]) return;
    delete storedOrders[scoped];
    setOnStorage('order_numbers', storedOrders);
  }, [selectedClient?.COD_CLIENTE]);

  const handleControlStock = useCallback(() => {
    productList?.forEach((entry) => {
      const [_, qtd] = entry?.item?.option_uid?.split('_');
      const fullStock = getLimit(entry?.item?.sku);
      const hasRegistered = stockLeft?.[entry?.item?.sku] as StockProps;
      const stock = stockLeft?.[entry?.item?.sku]?.stock_left as number;

      handleUpdateStock({
        product_code: entry?.item?.sku,
        stock: {
          stock_left:
            hasRegistered && stock === entry?.item?.quantity * Number(qtd)
              ? 0
              : hasRegistered && stock > 0
                ? stock - entry?.item?.quantity * Number(qtd)
                : !hasRegistered && fullStock === entry?.item?.quantity * Number(qtd)
                  ? 0
                  : fullStock - entry?.item?.quantity * Number(qtd),
        },
      });
    });
  }, [productList, getLimit, stockLeft, handleUpdateStock]);

  const storeOrderData = useCallback(
    (payloadToStore: OrderPayloadProps | null) => {
      try {
        if (!payloadToStore) return false;
        setOrderData(payloadToStore);
        setCartTotals(cartTotals);

        if (!selectedClientScope) {
          setOrderItems(productList ?? []);
          return true;
        }

        const pricingContext = {
          paymentCode,
          findPrice,
          getDiscountProduct,
          selectedClientScope,
          selectedClient,
          listaTributacao,
          checkoutComboPriceDeps,
        };

        const checkoutProductList = (productList ?? []).filter(
          (entry): entry is CartProductItem & { product: ProductProps } => Boolean(entry.product)
        );

        // Split feito aqui, antes do handleRemoveItems limpar o carrinho e comboCartItemsByUid.
        const enrichedOrderItems = checkoutProductList.flatMap((entry) => {
          const allocations = comboCartItemsByUid[entry.item.uid];
          const slices = buildComboDisplaySplitsForCartLine(entry.item, allocations);

          return slices.flatMap((slice) => {
            const instanceAllocations = getSliceAllocationsForCartLine(allocations, slice);

            const sliceQty = slice.displayQuantity || 0;
            const discountQty = slice.comboDiscountQuantity || 0;
            const regularQty = Math.max(sliceQty - discountQty, 0);

            if (discountQty > 0 && regularQty > 0) {
              const comboSubEntry = {
                ...entry,
                item: { ...entry.item, quantity: discountQty, uid: slice.displayKey },
              };
              const regularSubEntry = {
                ...entry,
                item: { ...entry.item, quantity: regularQty, uid: slice.displayKey },
              };

              return [
                {
                  ...comboSubEntry,
                  checkoutPricing: buildCheckoutDisplayPricingForEntry(comboSubEntry, {
                    ...pricingContext,
                    getAllocations: () => instanceAllocations,
                  }),
                },
                {
                  ...regularSubEntry,
                  checkoutPricing: buildCheckoutDisplayPricingForEntry(regularSubEntry, {
                    ...pricingContext,
                    getAllocations: () => undefined,
                  }),
                },
              ];
            }

            const subEntry = {
              ...entry,
              item: { ...entry.item, quantity: sliceQty, uid: slice.displayKey },
            };
            const allocationsForPricing = discountQty > 0 ? instanceAllocations : undefined;

            return [
              {
                ...subEntry,
                checkoutPricing: buildCheckoutDisplayPricingForEntry(subEntry, {
                  ...pricingContext,
                  getAllocations: () => allocationsForPricing,
                }),
              },
            ];
          });
        });

        setOrderItems(enrichedOrderItems);
        return true;
      } catch (error) {
        console.error('Erro ao salvar os dados no DB:', error);
        return false;
      }
    },
    [
      productList,
      setOrderData,
      setCartTotals,
      setOrderItems,
      cartTotals,
      paymentCode,
      findPrice,
      getDiscountProduct,
      selectedClientScope,
      selectedClient,
      listaTributacao,
      checkoutComboPriceDeps,
      comboCartItemsByUid,
    ]
  );

  const checkoutOrder = useCallback(async () => {
    setLoading(true);
    // Segurança adicional: mesmo que o botão esteja desabilitado na UI,
    // garantimos aqui que pedidos que estouram o limite não avancem
    if (blockByCredit) {
      setLoading(false);
      window.alert('Não foi possível finalizar: o total do pedido excede o limite de crédito disponível.');
      return;
    }
    let currentPayload = generateOrderPayload();

    // ————— Itens do carrinho que ficariam fora do pedido —————
    // O pedido sai do `productList`, que só tem as linhas cujo produto está aberto no dicionário. Uma
    // linha com quantidade cujo produto não carregou sumiria do pedido sem aviso nenhum.
    const listedUids = new Set(productList.map((entry) => entry.item.uid));
    const cartItemsAtCheckout: CartItems[] = selectedClientScope
      ? (cartDetails?.[selectedClientScope.COD_CLIENTE]?.items ?? [])
      : [];
    const notLoadedItems = cartItemsAtCheckout.filter(
      (item) => Number(item?.quantity) > 0 && !listedUids.has(item.uid)
    );
    if (notLoadedItems.length > 0) {
      window.alert(
        'Não foi possível carregar os dados de alguns itens do carrinho. ' +
          'Volte ao carrinho, confira os itens e tente novamente:' +
          notLoadedItems.map((item) => `\n- SKU: ${item.sku}`).join('')
      );
      setLoading(false);
      return;
    }

    if (currentPayload && !currentPayload.orders?.actions_pending?.length) {
      window.alert('Não há itens no carrinho para finalizar o pedido.');
      setLoading(false);
      return;
    }

    // ————— Helpers de validação —————
    const isMissing = (v: any) =>
      v === null ||
      v === undefined ||
      v === '' ||
      (typeof v === 'number' && !Number.isFinite(v)) ||
      (typeof v === 'string' && v.trim().toLowerCase() === 'nan');

    const missingFieldsBase = (it: any) => {
      const miss: string[] = [];
      if (isMissing(it.price)) miss.push('price');
      if (isMissing(it.unit_price)) miss.push('unit_price');
      if (isMissing(it.unit_price_per_item)) miss.push('unit_price_per_item');
      if (isMissing(it.integration_price)) miss.push('integration_price');
      return miss;
    };

    const missingFieldsFlex = (it: any) => {
      const miss: string[] = [];
      const hasPercentual = !!it.percentual_flex && `${it.percentual_flex}` !== '0';
      if (!hasPercentual) return miss;

      // Campos de flex que precisam existir
      if (isMissing(it.unit_price_flex)) miss.push('unit_price_flex');
      if (isMissing(it.unit_price_per_item_flex)) miss.push('unit_price_per_item_flex');
      if (isMissing(it.price_flex)) miss.push('price_flex');

      // integration_price_flex deve existir e ser > 0
      const ipf = it.integration_price_flex;
      const ipfNum = Number(ipf);
      if (ipf === undefined || ipf === null || ipf === '' || !Number.isFinite(ipfNum) || ipfNum <= 0) {
        miss.push('integration_price_flex');
      }

      // st_price_flex pode ser 0; só consideramos ausente se vier undefined/null/NaN/vazio
      // mas se tiver st_price base, ele se torna obrigatório
      const hasBaseSt = !isMissing(it.st_price) && Number(it.st_price) > 0;
      if (hasBaseSt && isMissing(it.st_price_flex)) {
        miss.push('st_price_flex');
      }

      return miss;
    };

    // ————— Validação dos itens —————
    const validateItems = (payload: OrderPayloadProps | null) => {
      const items = payload?.orders?.actions_pending || [];
      return (items as any[])
        .map((it) => {
          const baseMiss = missingFieldsBase(it);
          const flexMiss = missingFieldsFlex(it);
          return {
            sku: it.sku,
            product: it.product,
            missing: [...baseMiss, ...flexMiss],
            isFlexOnly: baseMiss.length === 0 && flexMiss.length > 0,
          };
        })
        .filter((p) => p.missing.length > 0);
    };

    let problems = validateItems(currentPayload);
    const hasFlexProblems = problems.some((p) => p.isFlexOnly);

    // Se houver apenas problemas de flex, tenta recalcular uma vez
    if (problems.length > 0 && hasFlexProblems) {
      currentPayload = generateOrderPayload(); // Força o recálculo
      problems = validateItems(currentPayload); // Revalida
    }

    if (problems.length > 0) {
      const details = problems.map((p) => `\n- ${p.product} (SKU: ${p.sku}) -> faltando: ${p.missing.join(', ')}`).join('');

      window.alert(
        'Há itens com campos de preço obrigatórios ausentes/inválidos. ' +
          'Revise antes de finalizar:' +
          details +
          '\n\nDica: remova e readicione o item no carrinho.'
      );
      setLoading(false);
      return;
    }

    // ————— Continua o fluxo normal —————
    const finalPayload = currentPayload;
    const storageSuccess = storeOrderData(finalPayload);

    if (!storageSuccess) {
      window.alert('Erro ao armazenar os dados do pedido. O checkout não pode ser finalizado.');
      setLoading(false);
      return;
    }

    /**
     * Persist the order to the durable queue (IndexedDB via ordersStore).
     * Returns true on success. On failure we MUST NOT navigate to /sucesso
     * — that's the silent-loss path the rewrite exists to eliminate.
     *
     * Readers load local orders from IndexedDB (`BF_SELLER_ORDERS`) via ordersStore.
     */
    const enqueueOrder = async (payload: OrderPayloadProps): Promise<boolean> => {
      try {
        const rca = getOnStorage('rca_usuario') as any;
        await ordersStore.enqueue(payload as any, {
          rcaCodPessoa: rca?.codPessoa != null ? String(rca.codPessoa) : null,
        });
        await handleRemoveItems();
        clearOrderNumber();
        return true;
      } catch (err) {
        console.error('[checkout] enqueueOrder failed — order NOT persisted', err);
        return false;
      }
    };

    const updateStorages = async () => {
      const atualEstoque = tables.current.estoque;
      const newEstoque =
        finalPayload?.orders.actions_pending?.reduce(
          (acc, item: any) => {
            const consumed = item.quantity * (item.quantity_multiplo || 1) || 0;
            return {
              ...acc,
              [item.sku]: (acc[item.sku] || 0) + consumed,
            };
          },
          {} as Record<number, number>
        ) || {};

      const updatedEstoque = atualEstoque.map((etq: any) => {
        return {
          ...etq,
          QTD_ESTOQUE: etq.QTD_ESTOQUE - (newEstoque?.[etq.COD_PRODUTO] || 0),
        };
      });
      await updateTable(updatedEstoque, 'estoque');

      // Atualiza SALDO_DISPONIVEL do cliente também no fluxo offline
      // para respeitar o limite em pedidos subsequentes enquanto estiver offline
      if (selectedClient) {
        const client = { ...selectedClient } as any;
        client.SALDO_DISPONIVEL = Number(((client.SALDO_DISPONIVEL || 0) - (cartTotals.total as number)).toFixed(2));
        setOnStorage('selected_client', client);
        setSelectedClient(client);

        const listClients = tables.current.cliente;
        const updatedList = listClients?.map((i: any) => (i.COD_CLIENTE === selectedClient?.COD_CLIENTE ? client : i));
        await updateTable(updatedList, 'cliente');
      }
    };

    // ────────────────────────────────────────────────────────────────────
    // INVARIANT: the order is durably persisted BEFORE we declare success.
    // Both online and offline paths enqueue first. The online path may
    // additionally POST and mark the order as `acked`. If enqueue fails,
    // we refuse to navigate to /sucesso — losing visibility is the failure
    // mode we want.
    // ────────────────────────────────────────────────────────────────────

    if (!finalPayload) {
      setLoading(false);
      console.error('Order payload is null after validation');
      return;
    }
    const persisted = await enqueueOrder(finalPayload);
    if (!persisted) {
      setLoading(false);
      setSucess(false);
      window.alert('Não foi possível salvar o pedido neste dispositivo. Tente novamente.');
      return;
    }

    const isOnline = await checkConnectivity();
    if (isOnline) {
      try {
        const response = (await axiosCtx
          .post('/integra-pedido', finalPayload)
          .then((res) => res)
          .catch((error) => {
            throw (
              error?.body?.message ||
              error?.response?.data?.message ||
              error?.message ||
              error?.data?.message ||
              error?.body ||
              error?.data ||
              error
            );
          })) as any;

        if (response?.data?.message) {
          // API ack: the order moved past `pending` in the durable store.
          // It is NOT yet confirmed by the ERP — reconcileAckedOrders will
          // verify via pedido-notifica-get and either delete (confirmed)
          // or revert to pending (retry).
          await ordersStore.markAcked(finalPayload.orders.order_number);

          clearClientDiscount();

          //novo saldo flex

          if (setFlex) setFlex((rcaSaldoFlex || 0) + discountAmountClient);

          const client = { ...selectedClient } as any;

          // calculo de preco para saldo ERP do usuario
          client.SALDO_DISPONIVEL = Number(((client.SALDO_DISPONIVEL || 0) - (cartTotals.total as number)).toFixed(2));

          setOnStorage('selected_client', client);
          setSelectedClient(client);

          const listClients = tables.current.cliente;
          const updatedList = listClients?.map((i: any) => (i.COD_CLIENTE === selectedClient?.COD_CLIENTE ? client : i));
          await updateTable(updatedList, 'cliente');

          const atualEstoque = tables.current.estoque;
          const newEstoque =
            finalPayload?.orders.actions_pending?.reduce(
              (acc, item: any) => {
                const qt = item.packaging.split('_')[1];
                const key = `${String(item.cod_produto as any)}`;
                const consumed =
                  item.quantity * (item.quantity_multiplo > 1 ? item.quantity_multiplo : Number(qt)) || 0;
                return {
                  ...acc,
                  [key]: (acc[key] || 0) + consumed,
                };
              },
              {} as Record<string, number>
            ) || {};

          const updatedEstoque = atualEstoque.map((etq: any) => {
            return {
              ...etq,
              QTD_ESTOQUE: etq.QTD_ESTOQUE - (newEstoque?.[Number(etq.COD_PRODUTO)] || 0),
            };
          });
          await updateTable(updatedEstoque, 'estoque');

          setSucess(true);
          clearOrderNumber();
          handleControlStock();
          setCanAccessSuccess(true);
          navigate(`/sucesso`);
        } else {
          // POST returned no `data.message` — order stays in `pending` for
          // the sync loop to retry. Already enqueued, so no loss.
          setLoading(false);
          setSucess(false);
          await registerEvent({
            tipoEvento: 'CHECKOUT_ERROR',
            obsEvento: `Um erro ocorreu ao fechar pedido: ${response?.data?.message}`,
            stEvento: 0,
          });

          await updateStorages();
          clearClientDiscount();
          if (setFlex) setFlex((rcaSaldoFlex || 0) + discountAmountClient);
          setLoading(false);
          setSucess(true);
          handleControlStock();
          setCanAccessSuccess(true);
          navigate(`/sucesso?st=err`);
        }
      } catch (error) {
        // Network/HTTP error: order is already enqueued; sync loop will retry.
        setLoading(false);
        setSucess(false);

        await registerEvent({
          tipoEvento: 'CHECKOUT_ERROR',
          obsEvento: `Um erro ocorreu ao fechar pedido. Tente novamente mais tarde: ${error}`,
          stEvento: 0,
        });

        await updateStorages();
        clearClientDiscount();
        if (setFlex) setFlex((rcaSaldoFlex || 0) + discountAmountClient);
        setLoading(false);
        setSucess(true);
        handleControlStock();
        setCanAccessSuccess(true);
        navigate(`/sucesso?st=err`);
      }
    } else {
      // Offline: already enqueued above. Update local state and report
      // success to the user — the sync loop will push it when connectivity
      // returns.
      try {
        await updateStorages();
        clearClientDiscount();
        if (setFlex) setFlex((rcaSaldoFlex || 0) + discountAmountClient);

        setLoading(false);
        setSucess(true);
        handleControlStock();
        setCanAccessSuccess(true);
        navigate(`/sucesso`);
      } catch (e) {
        setLoading(false);
        setSucess(false);
        console.error('Error finalising offline checkout (order already enqueued):', e);
        // Order is durable in IDB even if updateStorages/etc threw. Still navigate.
        setCanAccessSuccess(true);
        navigate(`/sucesso?st=err`);
      }
    }
  }, [
    generateOrderPayload,
    productList,
    cartDetails,
    selectedClientScope,
    storeOrderData,
    connectivityStatus,
    axiosCtx,
    erpNumber,
    selectedClient,
    clearClientDiscount,
    setFlex,
    rcaSaldoFlex,
    discountAmountClient,
    cartTotals,
    setSelectedClient,
    tables,
    updateTable,
    clearOrderNumber,
    handleControlStock,
    handleRemoveItems,
    setCanAccessSuccess,
    navigate,
    getDiscountProduct,
  ]);

  return {
    total,
    disabled,
    cartItems,
    conditions,
    productList,
    paymentCode,
    shippingData,
    checkoutOrder,
    setPaymentCode,
    creditLimitExceeded,
    erpNumber,
    setErpNumber,
  };
};
